import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { cn } from './model/derive';
import usePresence from './usePresence';
import useScrollLock from './useScrollLock';

/**
 * 오른쪽에서 밀려 들어오는 패널 — 스크림·Esc 로 닫히고, 열려 있는 동안 뒤 페이지는 스크롤되지 않는다.
 * modeless 면 스크림 없이 뒤 화면을 그대로 쓸 수 있다 (태스크 패널):
 *   - 다른 태스크 링크(?task=)를 누르면 닫지 않고 그 태스크로 바뀐다
 *   - 패널 바깥 빈 곳을 누르면 닫힌다 · 모달 · 팝오버 · 메뉴 · data-keep-panel 안을 누르면 그대로
 */
const KEEP = '[role="dialog"], [role="menu"], [role="listbox"], [role="tooltip"], [data-keep-panel], a[href*="task="]';
const SidePanel = ({ open, onClose, label, width = 'max-w-[600px]', z = 'z-[800]', className, modeless = false, children }) => {
  const { mounted, shown } = usePresence(open, 300);
  const asideRef = useRef(null);
  useScrollLock(mounted && !modeless);
  useEffect(() => {
    if (!open || !modeless) return undefined;
    const onDown = (e) => {
      const el = e.target;
      if (!(el instanceof Element) || !el.isConnected) return;
      if (asideRef.current?.contains(el) || el.closest(KEEP)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, modeless, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) =>
      e.key === 'Escape' &&
      !document.querySelector('[role="dialog"][aria-modal="true"]:not([data-side-panel])') &&
      onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;
  return ReactDOM.createPortal(
    <div className={cn('fixed inset-0', z, (!open || modeless) && 'pointer-events-none')}>
      {!modeless && (
        <div
          aria-hidden
          onClick={onClose}
          className={cn(
            'absolute inset-0 bg-black/30 transition-opacity duration-300 motion-reduce:transition-none',
            shown ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      <aside
        ref={asideRef}
        role='dialog'
        aria-modal={modeless ? undefined : 'true'}
        aria-label={label}
        data-side-panel
        className={cn(
          'absolute inset-y-0 right-0 flex w-full flex-col overscroll-contain border-l border-green-deep03/[0.12] bg-grayscale-gray00 shadow-popup-01',
          modeless && open && 'pointer-events-auto',
          'transition-transform duration-300 ease-out motion-reduce:transition-none',
          shown ? 'translate-x-0' : 'translate-x-full',
          width,
          className,
        )}
      >
        {children}
      </aside>
    </div>,
    document.body,
  );
};

export default SidePanel;
